import { useState, useMemo } from "react";
import { ChevronLeft, ChevronRight } from "lucide-react";
import { Button } from "@/components/ui/button";
import { PatientCalendarCard } from "@/components/PatientCalendarCard";
import { PatientDetailDrawer } from "@/components/PatientDetailDrawer";
import { Patient } from "@/components/PatientCard";
import { useHorizontalDrag } from "@/hooks/useHorizontalDrag";
import { cn } from "@/lib/utils";
import { usePatientsByMDDate } from "@/hooks/usePatients";
import { Skeleton } from "@/components/ui/skeleton";
import { startOfWeek, endOfWeek, startOfMonth, endOfMonth, addWeeks, subWeeks, addMonths, subMonths, format } from "date-fns";

const DAYS = ["MON", "TUE", "WED", "THU", "FRI", "SAT", "SUN"];

// Extended patient type with MD completion time
interface PatientWithMDTime extends Patient {
  mdCompletionTime: string | null;
}

export const PatientTimeline = () => {
  const [viewMode, setViewMode] = useState<"weekly" | "monthly">("weekly");
  const [currentWeekStart, setCurrentWeekStart] = useState(() => {
    return startOfWeek(new Date(), { weekStartsOn: 1 });
  });
  const [currentMonth, setCurrentMonth] = useState(() => {
    return startOfMonth(new Date());
  });

  const [selectedPatient, setSelectedPatient] = useState<Patient | null>(null);
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);

  const scrollRef = useHorizontalDrag();

  const startDate = viewMode === "weekly" 
    ? startOfWeek(currentWeekStart, { weekStartsOn: 1 })
    : startOfMonth(currentMonth);
    
  const endDate = viewMode === "weekly"
    ? endOfWeek(currentWeekStart, { weekStartsOn: 1 })
    : endOfMonth(currentMonth);

  // Use the new hook that fetches patients by MD completion date
  const { data: allPatients = [], isLoading } = usePatientsByMDDate(startDate, endDate);

  const weekDates = useMemo(() => {
    return Array.from({ length: 7 }, (_, i) => {
      const date = new Date(currentWeekStart);
      date.setDate(currentWeekStart.getDate() + i);
      return date;
    });
  }, [currentWeekStart]);

  const patientsByDay = useMemo(() => {
    const grouped: Record<string, PatientWithMDTime[]> = {};

    (allPatients as PatientWithMDTime[]).forEach((patient) => {
      // Use MD completion time as the primary grouping date.
      // If MD is not yet completed, fall back to expected discharge date
      // so future dates show awaiting / not-initiated patients.
      const dateToUse = patient.mdCompletionTime || patient.expectedDischarge;
      
      if (dateToUse) {
        try {
          const mdDate = new Date(dateToUse);
          // Format in IST timezone to get correct local date
          // This ensures patients marked at 1 AM IST appear on that IST date, not the previous UTC date
          const dateKey = mdDate.toLocaleDateString('en-CA', { timeZone: 'Asia/Kolkata' });
          if (!grouped[dateKey]) {
            grouped[dateKey] = [];
          }
          grouped[dateKey].push(patient);
        } catch (error) {
          console.error("Error parsing MD completion date:", dateToUse);
        }
      }
    });

    return grouped;
  }, [allPatients]);

  const handlePreviousWeek = () => {
    setCurrentWeekStart(subWeeks(currentWeekStart, 1));
  };

  const handleNextWeek = () => {
    setCurrentWeekStart(addWeeks(currentWeekStart, 1));
  };

  const handleToday = () => {
    if (viewMode === "weekly") {
      setCurrentWeekStart(startOfWeek(new Date(), { weekStartsOn: 1 }));
    } else {
      setCurrentMonth(startOfMonth(new Date()));
    }
  };

  const handlePreviousMonth = () => {
    setCurrentMonth(subMonths(currentMonth, 1));
  };

  const handleNextMonth = () => {
    setCurrentMonth(addMonths(currentMonth, 1));
  };

  const getDaysInMonth = (date: Date) => {
    const year = date.getFullYear();
    const month = date.getMonth();
    const daysInMonth = new Date(year, month + 1, 0).getDate();
    const firstDay = new Date(year, month, 1).getDay();
    const startOffset = firstDay === 0 ? 6 : firstDay - 1; // Adjust so Monday is first

    return { daysInMonth, startOffset };
  };

  const monthDates = useMemo(() => {
    if (viewMode === "weekly") return [];

    const { daysInMonth, startOffset } = getDaysInMonth(currentMonth);
    const dates: (Date | null)[] = [];

    // Add empty cells for offset
    for (let i = 0; i < startOffset; i++) {
      dates.push(null);
    }

    // Add all days of the month
    for (let i = 1; i <= daysInMonth; i++) {
      const date = new Date(currentMonth.getFullYear(), currentMonth.getMonth(), i);
      dates.push(date);
    }

    return dates;
  }, [currentMonth, viewMode]);

  const handlePatientClick = (patient: Patient) => {
    setSelectedPatient(patient);
    setIsDrawerOpen(true);
  };

  const displayMonth =
    viewMode === "weekly"
      ? weekDates[0].toLocaleDateString("en-US", { month: "long", year: "numeric" })
      : currentMonth.toLocaleDateString("en-US", { month: "long", year: "numeric" });
  const today = new Date().toDateString();

  if (isLoading) {
    return (
      <div className="min-h-screen bg-background">
        <div className="bg-card border-b border-border py-1 px-2">
          <div className="flex items-center justify-between">
            <Skeleton className="h-4 w-64" />
            <div className="flex items-center gap-2">
              <Skeleton className="h-7 w-28" />
              <Skeleton className="h-7 w-16" />
              <Skeleton className="h-7 w-32" />
            </div>
          </div>
        </div>
        {viewMode === "weekly" ? (
          <div className="flex h-[calc(100vh-100px)]">
            {Array.from({ length: 7 }).map((_, i) => (
              <div key={i} className={cn("flex-1 min-w-[140px] flex flex-col", i < 6 && "border-r border-border")}>
                <div className="text-center py-1.5 border-b border-border space-y-1">
                  <Skeleton className="h-3 w-8 mx-auto" />
                  <Skeleton className="h-6 w-6 rounded-full mx-auto" />
                  <Skeleton className="h-3 w-14 mx-auto" />
                </div>
                <div className="flex-1 p-1.5 space-y-1.5">
                  {Array.from({ length: Math.floor(Math.random() * 3) + 1 }).map((_, j) => (
                    <Skeleton key={j} className="h-16 w-full rounded-lg" />
                  ))}
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="p-2">
            <div className="grid grid-cols-7 gap-2">
              {DAYS.map((day) => (
                <div key={day} className="text-center py-2">
                  <Skeleton className="h-3 w-8 mx-auto" />
                </div>
              ))}
              {Array.from({ length: 35 }).map((_, i) => (
                <div key={i} className="h-[120px] sm:h-[140px] lg:h-[160px] bg-card border border-border rounded-lg flex flex-col overflow-hidden">
                  <div className="text-center py-1 border-b border-border">
                    <Skeleton className="h-5 w-5 rounded-full mx-auto" />
                  </div>
                  <div className="flex-1 p-0.5 space-y-1">
                    {i % 3 === 0 && <Skeleton className="h-10 w-full rounded" />}
                    {i % 5 === 0 && <Skeleton className="h-10 w-full rounded" />}
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <div className="bg-card border-b border-border py-1 px-2">
        <div className="flex items-center justify-between">
          <h2 className="text-sm font-semibold text-foreground">Patients Marked for Discharge This {viewMode === "weekly" ? "Week" : "Month"}</h2>

          <div className="flex items-center gap-2">
            <div className="flex items-center gap-1 border border-border rounded-md p-0.5">
              <Button
                variant={viewMode === "weekly" ? "secondary" : "ghost"}
                size="sm"
                onClick={() => setViewMode("weekly")}
                className="text-xs h-6 px-2"
              >
                Week
              </Button>
              <Button
                variant={viewMode === "monthly" ? "secondary" : "ghost"}
                size="sm"
                onClick={() => setViewMode("monthly")}
                className="text-xs h-6 px-2"
              >
                Month
              </Button>
            </div>

            <Button variant="outline" size="sm" onClick={handleToday} className="text-xs h-7 px-3">
              Today
            </Button>

            <div className="flex items-center gap-1">
              <Button
                variant="ghost"
                size="icon"
                onClick={viewMode === "weekly" ? handlePreviousWeek : handlePreviousMonth}
                className="h-7 w-7"
              >
                <ChevronLeft className="h-3 w-3" />
              </Button>

              <span className="text-xs font-medium text-foreground min-w-[100px] text-center">{displayMonth}</span>

              <Button
                variant="ghost"
                size="icon"
                onClick={viewMode === "weekly" ? handleNextWeek : handleNextMonth}
                className="h-7 w-7"
              >
                <ChevronRight className="h-3 w-3" />
              </Button>
            </div>
          </div>
        </div>
      </div>

      {/* Timeline */}
      {viewMode === "weekly" ? (
        <div
          ref={scrollRef}
          className="overflow-x-auto hide-scrollbar flex h-[calc(100vh-100px)]"
          style={{ scrollbarWidth: "none" }}
        >
          {weekDates.map((date, index) => {
            // Format in IST timezone to match the grouping
            const dateKey = date.toLocaleDateString('en-CA', { timeZone: 'Asia/Kolkata' });
            const patients = patientsByDay[dateKey] || [];
            const isToday = date.toDateString() === today;
            const dayName = DAYS[index];
            const dayNumber = date.getDate();

            return (
              <div
                key={dateKey}
                className={cn("flex-1 min-w-[140px] flex flex-col", index < 6 && "border-r border-border")}
              >
                {/* Day Header */}
                <div className="text-center py-1.5 border-b border-border">
                  <div className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground mb-1">
                    {dayName}
                  </div>
                  <div
                    className={cn(
                      "inline-flex items-center justify-center w-6 h-6 rounded-full text-sm font-medium",
                      isToday ? "bg-primary text-primary-foreground" : "text-foreground",
                    )}
                  >
                    {dayNumber}
                  </div>
                  {/* Patient count badge */}
                  <div className="text-[10px] text-muted-foreground mt-1 font-medium">
                    {patients.length} {patients.length === 1 ? 'patient' : 'patients'}
                  </div>
                </div>

                {/* Patient Cards */}
                <div className="flex-1 overflow-y-auto hide-scrollbar p-1.5 space-y-1.5 bg-background">
                  {patients.length > 0 ? (
                    patients.map((patient) => (
                      <PatientCalendarCard key={patient.id} patient={patient} onClick={handlePatientClick} />
                    ))
                  ) : (
                    <div className="text-center py-6 text-[10px] text-muted-foreground">No discharges</div>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      ) : (
        <div className="p-2">
          <div className="grid grid-cols-7 gap-2">
            {/* Day headers */}
            {DAYS.map((day) => (
              <div
                key={day}
                className="text-center py-2 text-[10px] font-semibold uppercase tracking-wider text-muted-foreground"
              >
                {day}
              </div>
            ))}

            {/* Calendar cells */}
            {monthDates.map((date, index) => {
              if (!date) {
                return <div key={`empty-${index}`} className="min-h-[100px] bg-muted/20" />;
              }

              // Format in IST timezone to match the grouping
              const dateKey = date.toLocaleDateString('en-CA', { timeZone: 'Asia/Kolkata' });
              const patients = patientsByDay[dateKey] || [];
              const isToday = date.toDateString() === today;
              const dayNumber = date.getDate();

              return (
                <div
                  key={dateKey}
                  className={cn(
                    "h-[120px] sm:h-[140px] lg:h-[160px] bg-card border border-border rounded-lg flex flex-col overflow-hidden",
                    isToday && "ring-2 ring-primary",
                  )}
                >
                  {/* Day number with patient count */}
                  <div className="text-center py-1 border-b border-border shrink-0">
                    <div className="flex items-center justify-center gap-1">
                      <div
                        className={cn(
                          "inline-flex items-center justify-center w-6 h-6 rounded-full text-xs font-medium",
                          isToday ? "bg-primary text-primary-foreground" : "text-foreground",
                        )}
                      >
                        {dayNumber}
                      </div>
                      {patients.length > 0 && (
                        <span className="text-[10px] text-muted-foreground font-medium">
                          ({patients.length})
                        </span>
                      )}
                    </div>
                  </div>

                  {/* Patient cards - scrollable within fixed height */}
                  <div className="flex-1 overflow-y-auto hide-scrollbar p-0.5 space-y-1">
                    {patients.length > 0
                      ? patients.map((patient) => (
                          <PatientCalendarCard key={patient.id} patient={patient} onClick={handlePatientClick} />
                        ))
                      : null}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Patient Detail Drawer */}
      {selectedPatient && (
        <PatientDetailDrawer patient={selectedPatient} open={isDrawerOpen} onOpenChange={setIsDrawerOpen} />
      )}
    </div>
  );
};
